/**
 * SpaceDocumentProvider - keeps one real-time space document (the board, a
 * sheet, a page) in a local Yjs document, in sync with the server.
 *
 * - Subscribes through an injected transport (`transport.ts` is the real
 *   WebSocket RPC one; tests use a fake). The first `sync` event carries the
 *   full state and the session's access; `update` and `awareness` events
 *   follow. Remote changes are applied with this provider as the origin, so
 *   they are never sent back.
 * - Local changes go into an ordered queue and are pushed one batch at a
 *   time. A batch leaves the queue only when the server acknowledges it
 *   (the server stores it first); a failed push is retried with backoff, and
 *   the queue survives reconnects. At every `sync` the queue is rebased on
 *   the server's state: whatever the server lacks (also edits stored offline
 *   in an earlier visit) becomes the head of the queue.
 * - With `view` access nothing is pushed; the editor should be read-only.
 * - Offline copies live in IndexedDB (`amoreg-space:<spaceId>:<docId>`)
 *   when a persistence factory is given; the provider subscribes once the
 *   copy is loaded, so the first sync can send what the server lacks.
 * - Presence (`awareness`) is pushed on change, throttled. The server
 *   overwrites the `user` field with the session's identity.
 *
 * Status: `connecting` until a sync arrives, `synced` while live, `offline`
 * while the transport is down or retrying, `closed` once access is lost or
 * the provider is destroyed.
 *
 * @module spaces/realtime/SpaceDocumentProvider
 */
import {
  SPACE_DOCUMENT_LIMITS,
  type SpaceDocumentAccess,
  type SpaceDocumentAudience,
  type SpaceDocumentStreamEvent,
} from "@amoreg/contracts";
import { fromBase64, toBase64 } from "lib0/buffer";
import {
  applyAwarenessUpdate,
  Awareness,
  encodeAwarenessUpdate,
  removeAwarenessStates,
} from "y-protocols/awareness";
import * as Y from "yjs";

import type { SpaceDocumentTransport } from "./transport";

export type SpaceDocumentStatus = "connecting" | "synced" | "offline" | "closed";

/** An offline copy of the document (y-indexeddb's shape). */
export interface SpaceDocumentPersistence {
  readonly whenSynced: Promise<unknown>;
  readonly destroy: () => unknown;
}

export type SpaceDocumentPersistenceFactory = (
  name: string,
  doc: Y.Doc,
) => SpaceDocumentPersistence | null;

export interface SpaceDocumentProviderOptions {
  readonly spaceId: string;
  readonly docId: string;
  readonly transport: SpaceDocumentTransport;
  readonly persistence?: SpaceDocumentPersistenceFactory;
  /** Minimum gap between awareness pushes. */
  readonly awarenessThrottleMs?: number;
  /** First retry delay after a failed push or subscription; doubles to `retryMaxMs`. */
  readonly retryInitialMs?: number;
  readonly retryMaxMs?: number;
  /** How long to wait for the offline copy before subscribing anyway. */
  readonly persistenceTimeoutMs?: number;
}

interface SpaceDocumentProviderEvents {
  readonly status: SpaceDocumentStatus;
  readonly access: SpaceDocumentAccess | null;
  /** The first sync of this provider arrived. */
  readonly synced: void;
  /** A push the server refused for good (too large, invalid, read-only). */
  readonly rejected: unknown;
}

type Listener<T> = (value: T) => void;

interface PendingUpdate {
  readonly update: Uint8Array;
}

/** The IndexedDB name of a document's offline copy. */
export const spaceDocumentStorageName = (spaceId: string, docId: string) =>
  `amoreg-space:${spaceId}:${docId}`;

const errorTag = (error: unknown): string | null =>
  typeof error === "object" && error !== null && "_tag" in error && typeof error._tag === "string"
    ? error._tag
    : null;

interface DeleteRanges {
  readonly clients: Map<number, ReadonlyArray<{ readonly clock: number; readonly len: number }>>;
}

/** Is every range deleted in `inner` also deleted in `outer` (sorted, merged)? */
const deletionsCovered = (inner: DeleteRanges, outer: DeleteRanges): boolean => {
  for (const [client, ranges] of inner.clients) {
    const covering = outer.clients.get(client) ?? [];
    for (const range of ranges) {
      let cursor = range.clock;
      const end = range.clock + range.len;
      for (const candidate of covering) {
        if (candidate.clock <= cursor && candidate.clock + candidate.len > cursor) {
          cursor = candidate.clock + candidate.len;
        }
        if (cursor >= end) break;
      }
      if (cursor < end) return false;
    }
  }
  return true;
};

/** Does `doc` hold anything (content or deletions) the server's state lacks? */
const holdsMoreThan = (doc: Y.Doc, serverState: Uint8Array): boolean => {
  const server = Y.decodeStateVector(Y.encodeStateVectorFromUpdate(serverState));
  for (const [client, clock] of Y.decodeStateVector(Y.encodeStateVector(doc))) {
    if (clock > (server.get(client) ?? 0)) return true;
  }
  return !deletionsCovered(
    Y.createDeleteSetFromStructStore(doc.store) as unknown as DeleteRanges,
    Y.decodeUpdate(serverState).ds as unknown as DeleteRanges,
  );
};

export class SpaceDocumentProvider {
  readonly doc: Y.Doc;
  readonly awareness: Awareness;
  readonly spaceId: string;
  readonly docId: string;

  status: SpaceDocumentStatus = "connecting";
  access: SpaceDocumentAccess | null = null;
  audience: SpaceDocumentAudience | null = null;
  /** True once the first sync has arrived. */
  synced = false;

  private refusedEdits = false;
  private readonly transport: SpaceDocumentTransport;
  private readonly persistence: SpaceDocumentPersistence | null;
  private readonly awarenessThrottleMs: number;
  private readonly retryInitialMs: number;
  private readonly retryMaxMs: number;
  private readonly listeners = new Map<keyof SpaceDocumentProviderEvents, Set<Listener<never>>>();
  private queue: Array<PendingUpdate> = [];
  private inFlight = false;
  private connection: string | null = null;
  private unsubscribe: (() => void) | null = null;
  private pushRetryDelay: number;
  private pushRetryTimer: ReturnType<typeof setTimeout> | null = null;
  private subscribeRetryDelay: number;
  private subscribeRetryTimer: ReturnType<typeof setTimeout> | null = null;
  private awarenessTimer: ReturnType<typeof setTimeout> | null = null;
  private lastAwarenessPush = 0;
  private destroyed = false;

  constructor(options: SpaceDocumentProviderOptions) {
    this.spaceId = options.spaceId;
    this.docId = options.docId;
    this.transport = options.transport;
    this.awarenessThrottleMs = options.awarenessThrottleMs ?? 100;
    this.retryInitialMs = options.retryInitialMs ?? 500;
    this.retryMaxMs = options.retryMaxMs ?? 30_000;
    this.pushRetryDelay = this.retryInitialMs;
    this.subscribeRetryDelay = this.retryInitialMs;
    this.doc = new Y.Doc();
    this.awareness = new Awareness(this.doc);
    this.doc.on("update", this.onDocumentUpdate);
    this.awareness.on("update", this.onAwarenessUpdate);
    this.persistence =
      options.persistence?.(spaceDocumentStorageName(this.spaceId, this.docId), this.doc) ?? null;
    if (this.persistence === null) {
      this.connect();
    } else {
      // Subscribe once the offline copy is in, so the first sync can send
      // what the server lacks; never wait on it for long.
      const timeout = new Promise((resolve) =>
        setTimeout(resolve, options.persistenceTimeoutMs ?? 1_500),
      );
      void Promise.race([this.persistence.whenSynced, timeout])
        .catch(() => undefined)
        .then(() => this.connect());
    }
  }

  /** Listen to `status`, `access`, `synced` or `rejected`; returns the unsubscribe. */
  on<K extends keyof SpaceDocumentProviderEvents>(
    event: K,
    listener: Listener<SpaceDocumentProviderEvents[K]>,
  ): () => void {
    let set = this.listeners.get(event);
    if (set === undefined) {
      set = new Set();
      this.listeners.set(event, set);
    }
    set.add(listener as Listener<never>);
    return () => {
      set.delete(listener as Listener<never>);
    };
  }

  /** Local updates not yet acknowledged by the server. */
  get pendingUpdates(): number {
    return this.queue.length;
  }

  /**
   * True once the server refused a local edit for good (too large, invalid,
   * or sent after access became view only): this screen shows something the
   * server does not hold, until the document is opened again.
   */
  get hasRefusedEdits(): boolean {
    return this.refusedEdits;
  }

  /**
   * True once the server holds every local edit (live, nothing queued, none
   * refused); false when that has not happened within `timeoutMs`, an edit
   * was refused, or the document closed. A server-side checkpoint or export
   * taken after this includes what the person sees.
   */
  async whenAcknowledged(timeoutMs = 10_000, intervalMs = 100): Promise<boolean> {
    const deadline = Date.now() + timeoutMs;
    while (true) {
      if (this.destroyed || this.status === "closed" || this.refusedEdits) return false;
      if (this.status === "synced" && this.queue.length === 0) return true;
      if (Date.now() >= deadline) return false;
      await new Promise((resolve) => setTimeout(resolve, intervalMs));
    }
  }

  destroy(): void {
    if (this.destroyed) return;
    this.setStatus("closed");
    this.destroyed = true;
    this.clearTimers();
    this.unsubscribe?.();
    this.unsubscribe = null;
    this.doc.off("update", this.onDocumentUpdate);
    this.awareness.off("update", this.onAwarenessUpdate);
    this.awareness.destroy();
    this.persistence?.destroy();
    this.doc.destroy();
    this.listeners.clear();
  }

  // -- Subscription --------------------------------------------------------

  private connect(): void {
    if (this.destroyed || this.status === "closed") return;
    this.unsubscribe?.();
    this.connection = null;
    this.setStatus(this.status === "offline" ? "offline" : "connecting");
    this.unsubscribe = this.transport.subscribe(
      { spaceId: this.spaceId, docId: this.docId },
      {
        onEvent: (event) => this.onEvent(event),
        onError: (error) => this.onSubscriptionError(error),
        onConnection: (online) => this.onConnection(online),
      },
    );
  }

  private onEvent(event: SpaceDocumentStreamEvent): void {
    if (this.destroyed) return;
    switch (event.type) {
      case "sync":
        return this.onSync(event.state, event.access, event.audience, event.connection);
      case "update":
        Y.applyUpdate(this.doc, fromBase64(event.update), this);
        return;
      case "awareness":
        applyAwarenessUpdate(this.awareness, fromBase64(event.update), this);
        return;
      case "closed":
        return this.close();
    }
  }

  private onSync(
    encodedState: string,
    access: SpaceDocumentAccess,
    audience: SpaceDocumentAudience,
    connection: string,
  ): void {
    const state = fromBase64(encodedState);
    Y.applyUpdate(this.doc, state, this);
    this.connection = connection;
    this.audience = audience;
    this.subscribeRetryDelay = this.retryInitialMs;
    this.setAccess(access);
    if (access === "edit") {
      // Rebase the queue on the server's state: one update with everything
      // the server lacks covers every queued change it has not stored.
      this.queue = holdsMoreThan(this.doc, state)
        ? [{ update: Y.encodeStateAsUpdate(this.doc, Y.encodeStateVectorFromUpdate(state)) }]
        : [];
    } else {
      this.queue = [];
    }
    this.setStatus("synced");
    if (!this.synced) {
      this.synced = true;
      this.emit("synced", undefined);
    }
    this.clearPushRetry();
    this.pushRetryDelay = this.retryInitialMs;
    void this.flush();
    if (this.awareness.getLocalState() !== null) this.pushAwarenessNow();
  }

  private onConnection(online: boolean): void {
    if (this.destroyed || this.status === "closed") return;
    if (online) {
      if (this.status === "offline") this.setStatus("connecting");
      return;
    }
    this.connection = null;
    this.setStatus("offline");
    this.dropRemoteAwareness();
  }

  private onSubscriptionError(error: unknown): void {
    if (this.destroyed || this.status === "closed") return;
    if (errorTag(error) === "SpaceDocumentNotFoundError") {
      this.close();
      return;
    }
    this.connection = null;
    this.setStatus("offline");
    this.dropRemoteAwareness();
    const delay = this.subscribeRetryDelay;
    this.subscribeRetryDelay = Math.min(this.subscribeRetryDelay * 2, this.retryMaxMs);
    // One retry at a time: a late error from an older subscription must not
    // schedule a second reconnect beside the pending one.
    if (this.subscribeRetryTimer !== null) clearTimeout(this.subscribeRetryTimer);
    this.subscribeRetryTimer = setTimeout(() => {
      this.subscribeRetryTimer = null;
      this.connect();
    }, delay);
  }

  /** Access is gone (or the document): stop for good. */
  private close(): void {
    this.queue = [];
    this.connection = null;
    this.clearTimers();
    this.unsubscribe?.();
    this.unsubscribe = null;
    this.dropRemoteAwareness();
    this.setStatus("closed");
  }

  // -- Local updates -------------------------------------------------------

  private readonly onDocumentUpdate = (update: Uint8Array, origin: unknown): void => {
    // Remote changes and the offline copy loading are not ours to send.
    if (origin === this || (this.persistence !== null && origin === this.persistence)) return;
    if (this.destroyed || this.status === "closed" || this.access === "view") return;
    this.queue.push({ update });
    void this.flush();
  };

  /** Push queued updates in order, one batch in flight at a time. */
  private async flush(): Promise<void> {
    if (this.inFlight || this.destroyed || this.pushRetryTimer !== null) return;
    if (this.status !== "synced" || this.access !== "edit" || this.connection === null) return;
    if (this.queue.length === 0) return;
    const batch: Array<PendingUpdate> = [];
    let bytes = 0;
    for (const item of this.queue) {
      if (batch.length > 0 && bytes + item.update.byteLength > SPACE_DOCUMENT_LIMITS.updateBytes) {
        break;
      }
      batch.push(item);
      bytes += item.update.byteLength;
    }
    const update =
      batch.length === 1 ? batch[0]!.update : Y.mergeUpdates(batch.map((item) => item.update));
    this.inFlight = true;
    const connection = this.connection;
    let retryAfter: number | null = null;
    try {
      await this.transport.pushUpdate({
        spaceId: this.spaceId,
        docId: this.docId,
        update: toBase64(update),
        connection,
      });
      this.removeFromQueue(batch);
      this.pushRetryDelay = this.retryInitialMs;
    } catch (error) {
      // A push lost with an old connection is retried at once on the new
      // one; the sync in between already rebased the queue.
      retryAfter = this.connection !== connection ? null : this.onPushError(error, batch);
    } finally {
      this.inFlight = false;
    }
    if (this.destroyed) return;
    if (retryAfter !== null) {
      this.pushRetryTimer = setTimeout(() => {
        this.pushRetryTimer = null;
        void this.flush();
      }, retryAfter);
      return;
    }
    void this.flush();
  }

  /** Handle a refused push; returns the retry delay, or null for none. */
  private onPushError(error: unknown, batch: ReadonlyArray<PendingUpdate>): number | null {
    switch (errorTag(error)) {
      case "SpaceDocumentNotFoundError":
        this.close();
        return null;
      case "SpaceDocumentReadOnlyError":
        if (this.queue.length > 0) this.refusedEdits = true;
        this.queue = [];
        this.setAccess("view");
        this.emit("rejected", error);
        return null;
      case "SpaceDocumentTooLargeError":
      case "SpaceDocumentInvalidUpdateError":
        // The server will never take these; keep the rest going. This screen
        // now shows edits the server does not hold.
        this.refusedEdits = true;
        this.removeFromQueue(batch);
        this.emit("rejected", error);
        return null;
      case "SpaceDocumentRateLimitedError": {
        const retryAfterMs = (error as { readonly retryAfterMs?: unknown }).retryAfterMs;
        return typeof retryAfterMs === "number" ? Math.max(retryAfterMs, 50) : this.nextPushDelay();
      }
      default:
        return this.nextPushDelay();
    }
  }

  private nextPushDelay(): number {
    const delay = this.pushRetryDelay;
    this.pushRetryDelay = Math.min(this.pushRetryDelay * 2, this.retryMaxMs);
    return delay;
  }

  private removeFromQueue(batch: ReadonlyArray<PendingUpdate>): void {
    const sent = new Set(batch);
    this.queue = this.queue.filter((item) => !sent.has(item));
  }

  // -- Awareness -----------------------------------------------------------

  private readonly onAwarenessUpdate = (
    changes: { readonly added: number[]; readonly updated: number[]; readonly removed: number[] },
    origin: unknown,
  ): void => {
    if (origin !== "local" || this.destroyed) return;
    const mine = this.doc.clientID;
    if (
      !changes.added.includes(mine) &&
      !changes.updated.includes(mine) &&
      !changes.removed.includes(mine)
    ) {
      return;
    }
    if (this.awarenessTimer !== null) return;
    const wait = Math.max(0, this.lastAwarenessPush + this.awarenessThrottleMs - Date.now());
    this.awarenessTimer = setTimeout(() => {
      this.awarenessTimer = null;
      this.pushAwarenessNow();
    }, wait);
  };

  private pushAwarenessNow(): void {
    if (this.destroyed || this.status !== "synced" || this.connection === null) return;
    this.lastAwarenessPush = Date.now();
    void this.transport
      .pushAwareness({
        spaceId: this.spaceId,
        docId: this.docId,
        update: toBase64(encodeAwarenessUpdate(this.awareness, [this.doc.clientID])),
        connection: this.connection,
      })
      .catch(() => undefined);
  }

  /** Others' carets are stale once we are cut off; the next sync resends them. */
  private dropRemoteAwareness(): void {
    const others = [...this.awareness.getStates().keys()].filter(
      (client) => client !== this.doc.clientID,
    );
    if (others.length > 0) removeAwarenessStates(this.awareness, others, this);
  }

  // -- Plumbing ------------------------------------------------------------

  private setStatus(status: SpaceDocumentStatus): void {
    if (this.status === status || (this.status === "closed" && status !== "closed")) return;
    this.status = status;
    this.emit("status", status);
  }

  private setAccess(access: SpaceDocumentAccess): void {
    if (this.access === access) return;
    this.access = access;
    this.emit("access", access);
  }

  private emit<K extends keyof SpaceDocumentProviderEvents>(
    event: K,
    value: SpaceDocumentProviderEvents[K],
  ): void {
    for (const listener of this.listeners.get(event) ?? []) {
      (listener as Listener<SpaceDocumentProviderEvents[K]>)(value);
    }
  }

  private clearPushRetry(): void {
    if (this.pushRetryTimer !== null) clearTimeout(this.pushRetryTimer);
    this.pushRetryTimer = null;
  }

  private clearTimers(): void {
    this.clearPushRetry();
    if (this.subscribeRetryTimer !== null) clearTimeout(this.subscribeRetryTimer);
    this.subscribeRetryTimer = null;
    if (this.awarenessTimer !== null) clearTimeout(this.awarenessTimer);
    this.awarenessTimer = null;
  }
}
